import { users as usersApi } from '../services/api';
import { usePersistedOrder } from './usePersistedOrder';

// v2 since the persona regroup (migration 20260928-04): the reconcile uploads a
// non-empty cache when the server is empty, which would re-save everyone's OLD order.
const STORAGE_KEY = 'atlas-sidebar-order-v2';

/**
 * Apply a saved sidebar ordering to a list of items keyed by `getKey`.
 *
 * Items whose key appears in `order` are placed first, in the saved order.
 * Items NOT in the saved order keep their default relative position and are
 * appended after the ordered ones — so newly-added nav items surface at the
 * bottom of the reorderable list instead of vanishing.
 */
export function applyOrder<T>(items: T[], order: string[], getKey: (item: T) => string): T[] {
  if (order.length === 0) return items;
  const rank = new Map(order.map((key, i) => [key, i]));
  const known: T[] = [];
  const unknown: T[] = [];
  for (const item of items) {
    if (rank.has(getKey(item))) known.push(item);
    else unknown.push(item);
  }
  known.sort((a, b) => rank.get(getKey(a))! - rank.get(getKey(b))!);
  return [...known, ...unknown];
}

const load = () => usersApi.getSidebarOrder();
const save = (order: string[]) => usersApi.setSidebarOrder(order);

/**
 * Per-user ordering of the main sidebar nav items (nav `to` paths in display
 * order; items absent from it fall back to their config position, see `applyOrder`).
 */
export function useSidebarOrder(): {
  order: string[];
  setOrder: (order: string[]) => void;
  previousOrder: string[] | null;
} {
  return usePersistedOrder(STORAGE_KEY, load, save);
}
